import React, { useMemo, useCallback, useState } from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { SelectionList } from '../SelectionList'
import useSelection, { SelectionMode } from '../OptimizedGroupsTree/hooks/useSelection'
import { RootState, ITag } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { NoResults } from '../GroupsTree/NoResults'
import { SearchInput } from 'components/ui/Input/SearchInput'
import { Button } from 'components/ui/Button'
import { TagRow } from './TagRow'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'

const getId = (tag: ITag) => tag.sk

const EMPTY_SELECTED_IDS: string[] = []

interface TagsListProps {
  selectionSections?: { selected: string; unselected: string }
  showSearch?: boolean
  searchWidth?: string
  searchActions?: React.ReactNode
  selectionMode?: SelectionMode
  onSelectionChange?: (selectedIds: string[]) => void
  initialSelectedIds?: string[]
  listHeight?: number
  // Restrict the list to these tag ids (e.g. the caller's org-tree scope). Undefined = show all tags.
  allowedTagIds?: string[]
}

const TagsList: React.FC<TagsListProps> = ({
  selectionSections,
  showSearch = true,
  searchWidth = '100%',
  searchActions,
  selectionMode = SelectionMode.MULTIPLE,
  initialSelectedIds = EMPTY_SELECTED_IDS,
  listHeight = 600,
  onSelectionChange,
  allowedTagIds,
}) => {
  const isRtl = useSelector(isRtlSelector)
  const { tags: allTags } = useSelector((state: RootState) => state.config.config)
  const [searchTerm, setSearchTerm] = useState('')
  const t_select_all = useLocaleText('select_all_stores_button')
  const t_clear = useLocaleText('group_unselecte_filter')
  const searchLabel = useLocaleText('search')

  const allowedTagIdsSet = useMemo(() => (allowedTagIds ? new Set(allowedTagIds) : null), [allowedTagIds])
  const tags = useMemo(
    () => (allowedTagIdsSet ? (allTags || []).filter((tag) => allowedTagIdsSet.has(tag.sk)) : allTags),
    [allTags, allowedTagIdsSet]
  )

  const allTagsIds = useMemo(() => tags?.map((tag) => tag.sk) || [], [tags])

  const selectionOptions = useMemo(
    () => ({
      mode: selectionMode,
      initialSelectedIds,
      allIds: allTagsIds,
      onSelectionChange,
    }),
    [selectionMode, initialSelectedIds, allTagsIds, onSelectionChange]
  )

  const { selectedIds, toggleSelection, isSelected, selectAll, clearSelection, isAllSelected } =
    useSelection(selectionOptions)

  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) return tags || []

    const lowerSearchTerm = searchTerm.toLowerCase()
    return (tags || []).filter((tag) => tag.name.toLowerCase().includes(lowerSearchTerm))
  }, [tags, searchTerm])

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      clearSelection()
    } else {
      selectAll()
    }
  }, [clearSelection, isAllSelected, selectAll])

  const renderItem = useCallback(
    (tag: ITag, style: React.CSSProperties) => {
      return (
        <TagRow
          tag={tag}
          isSelected={isSelected(tag.sk)}
          toggleSelection={toggleSelection}
          style={style}
          key={tag.sk}
        />
      )
    },
    [isSelected, toggleSelection]
  )

  return (
    <Box>
      {showSearch && (
        <Flex w="100%" flexDir="row" alignItems="center" justifyContent="center" p={2} style={{ gap: 8 }}>
          <Box width={searchWidth} minWidth="0">
            <SearchInput
              onChange={handleSearch}
              value={searchTerm}
              onClear={() => setSearchTerm('')}
              placeholder={searchLabel}
              aria-label={searchLabel}
              direction={isRtl ? 'rtl' : 'ltr'}
            />
          </Box>
          {selectionMode === SelectionMode.MULTIPLE && !searchTerm && (
            <Button
              variant="ghost"
              type="button"
              h="36px"
              w="max-content"
              minWidth="100px"
              onClick={searchActions ? selectAll : toggleSelectAll}
              borderRadius="5px"
              fontWeight="normal"
              borderColor={colors.greyLight}
            >
              {isAllSelected && !searchActions ? t_clear : t_select_all}
            </Button>
          )}
          {searchActions}
        </Flex>
      )}

      <Box>
        {filteredTags.length > 0 ? (
          <SelectionList
            items={filteredTags}
            getId={getId}
            selectedIds={selectedIds}
            sections={selectionSections}
            direction={isRtl ? 'rtl' : 'ltr'}
            height={listHeight}
            renderItem={renderItem}
          />
        ) : (
          <NoResults />
        )}
      </Box>
    </Box>
  )
}

export default TagsList
